import { useMemo, useEffect } from "react";
import { X } from "lucide-react";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ReferenceLine, ResponsiveContainer, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Legend, Cell } from "recharts";
import { Badge, tooltipStyle, tickStyle } from "@/components/kit";
import { statsOf, paramStats, histogram, groupBy, fmtN, fmtPct, fmtInt, monthLabel, bandOf, BAND_COLOR, BAND_LABEL, bandTone, lagDays, C } from "./qUtils";

const Drawer = ({ title, sub, badge, onClose, children, testid }) => {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
  <div className="fixed inset-0 z-50 flex justify-end" data-testid={testid}>
    <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
    <aside data-testid={`${testid}-panel`} className="relative flex h-full w-full max-w-[1500px] flex-col overflow-y-auto border-l border-thborder/12 bg-thsolid shadow-2xl lg:w-[88vw] xl:w-[80vw]">
      <div className="sticky top-0 z-10 flex items-start justify-between gap-4 border-b border-thborder/10 bg-thsolid/90 px-6 py-5 backdrop-blur-xl">
        <div><h2 className="font-heading text-2xl font-extrabold text-thstrong">{title}</h2><p className="mk-sub mt-1">{sub}</p></div>
        <div className="flex items-center gap-3">{badge}<button data-testid={`${testid}-close`} onClick={onClose} className="rounded-full border border-thborder/12 p-2 text-thmute hover:text-thcoral"><X className="h-4 w-4" /></button></div>
      </div>
      <div className="space-y-6 p-6">{children}</div>
    </aside>
  </div>
  );
};

const Chips = ({ items }) => (
  <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
    {items.map((c) => (
      <div key={c.label} className="mk-card p-3"><p className="mk-label">{c.label}</p><p className="mt-1.5 font-heading text-xl font-bold" style={{ color: c.color || "rgb(var(--th-strong))" }}>{c.v}</p></div>
    ))}
  </div>
);

const Section = ({ title, children }) => <div className="mk-card p-4"><p className="mk-label mb-3">{title}</p>{children}</div>;

const monthlySeries = (rows, t) => {
  const by = {};
  rows.forEach((r) => { (by[r.d.slice(0, 7)] = by[r.d.slice(0, 7)] || []).push(r); });
  return Object.keys(by).sort().map((m) => ({ m: monthLabel(m), ...statsOf(by[m], t) }));
};

export function QAgentDrawer({ data, cisco, onClose }) {
  const t = data.targets;
  const rows = useMemo(() => data.evals.filter((e) => e.c === cisco).sort((a, b) => (a.d < b.d ? 1 : -1)), [data, cisco]);
  const qsl = useMemo(() => data.qsl.filter((q) => q.c === cisco), [data, cisco]);
  const k = useMemo(() => statsOf(rows, t, qsl), [rows, t, qsl]);
  const latest = rows[0];
  const team = useMemo(() => data.evals.filter((e) => e.tl === latest?.tl), [data, latest]);
  const radar = useMemo(() => {
    const mine = paramStats(rows, data.params), theirs = paramStats(team, data.params);
    return mine.map((p, i) => ({ p: `${p.n}.`, label: p.label, agent: p.pass ?? 0, team: theirs[i].pass ?? 0 }));
  }, [rows, team, data.params]);
  const timeline = useMemo(() => [...rows].reverse().map((r) => ({ d: r.d.slice(5), score: r.score, fatal: r.fatal })), [rows]);
  const months = useMemo(() => monthlySeries(rows, t), [rows, t]);
  if (!latest) return null;
  const b = bandOf(k.avg, t);

  return (
    <Drawer testid="q-agent-drawer" title={latest.agent} sub={`Cisco ${cisco} · ${latest.lob} · ${latest.loc || "Unknown"} · TL ${latest.tl}${latest.tl2 ? ` / ${latest.tl2}` : ""}`} badge={<Badge tone={bandTone(b)}>{BAND_LABEL[b]}</Badge>} onClose={onClose}>
      <Chips items={[
        { label: "Evaluations", v: fmtInt(k.evals), color: C.blue }, { label: "Avg score", v: fmtN(k.avg), color: BAND_COLOR[b] },
        { label: "S / NC / C", v: `${rows.filter((r) => r.rt === "S").length} / ${rows.filter((r) => r.rt === "NC").length} / ${rows.filter((r) => r.rt === "C").length}`, color: C.teal },
        { label: "Compliance % (critical)", v: fmtPct(rows.length ? (100 * rows.filter((r) => r.rt === "C").length) / rows.length : null), color: rows.some((r) => r.rt === "C") ? C.coral : C.mint },
      ]} />
      <div className="grid gap-6 lg:grid-cols-2" data-testid="q-agent-drawer-charts">
      <Section title="Score timeline · all uploaded months">
        <ResponsiveContainer width="100%" height={220}>
          <LineChart data={timeline} margin={{ left: -20, right: 10 }}>
            <XAxis dataKey="d" tick={tickStyle} interval="preserveStartEnd" />
            <YAxis domain={[0, 100]} tick={tickStyle} />
            <Tooltip contentStyle={tooltipStyle} />
            <ReferenceLine y={t.pmt} stroke={C.teal} strokeDasharray="4 4" />
            <Line type="monotone" dataKey="score" stroke={C.amber} strokeWidth={2} dot={{ r: 3 }} />
          </LineChart>
        </ResponsiveContainer>
        {months.length > 1 && <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3" data-testid="q-agent-drawer-months">{months.map((m) => <div key={m.m} className="rounded-lg bg-thhover/6 px-2.5 py-1.5"><p className="mk-sub">{m.m}</p><p className="font-mono-hud text-xs text-thstrong">{fmtN(m.avg)} <span className="text-thdim">· {m.evals} evals</span></p></div>)}</div>}
      </Section>
      <Section title="Parameter radar · pass % vs team">
          <ResponsiveContainer width="100%" height={260}>
            <RadarChart data={radar} outerRadius={90}>
              <PolarGrid stroke="rgb(var(--th-border) / 0.15)" />
              <PolarAngleAxis dataKey="p" tick={tickStyle} />
              <PolarRadiusAxis domain={[0, 100]} tick={false} axisLine={false} />
              <Radar name="Agent" dataKey="agent" stroke={C.amber} fill={C.amber} fillOpacity={0.35} />
              <Radar name="Team" dataKey="team" stroke={C.teal} fill={C.teal} fillOpacity={0.15} />
              <Legend wrapperStyle={{ fontSize: 9, fontFamily: "JetBrains Mono" }} />
              <Tooltip contentStyle={tooltipStyle} formatter={(v) => fmtPct(v, 0)} />
            </RadarChart>
          </ResponsiveContainer>
          <p className="mk-sub">{radar.map((r) => `${r.p} ${r.label}`).join(" · ")}</p>
      </Section>
      </div>
      <Section title={`Recent evaluations · ${rows.length}`}>
          <div className="max-h-[520px] overflow-auto"><table className="mk-table">
            <thead><tr><th>Interaction</th><th>Evaluated</th><th className="text-right">Lag</th><th>Skill</th><th>Evaluator</th><th className="text-right">Dur / holds</th><th className="text-right">Score</th><th>Rating</th></tr></thead>
            <tbody>
              {rows.slice(0, 60).map((r, i) => (
                <tr key={i} title={r.rem || undefined}><td className="whitespace-nowrap font-mono-hud text-thdim">{r.sd || "—"}</td><td className="whitespace-nowrap font-mono-hud text-thdim">{r.d}</td>
                  <td className="text-right font-mono-hud" style={{ color: lagDays(r) > 30 ? C.amber : undefined }}>{lagDays(r) == null ? "—" : `${lagDays(r)}d`}</td>
                  <td className="max-w-[160px] truncate text-xs" title={`${r.form} · ${r.skill}`}>{r.skill}</td><td className="max-w-[140px] truncate text-xs">{r.ev}</td>
                  <td className="whitespace-nowrap text-right font-mono-hud text-thdim">{r.dur != null ? `${Math.round(r.dur)}s` : "—"}{r.holds != null ? ` / ${r.holds}` : ""}</td>
                  <td className="text-right font-mono-hud font-semibold" style={{ color: BAND_COLOR[bandOf(r.score, t)] }}>{fmtN(r.score, 0)}</td>
                  <td>{r.rt === "C" ? <Badge tone="bad">Critical</Badge> : r.rt === "NC" ? <Badge tone="warn">Non-Critical</Badge> : r.rt === "S" ? <Badge tone="good">Standard</Badge> : <span className="text-xs text-thdim">{r.rating || "—"}</span>}</td></tr>
              ))}
            </tbody>
          </table></div>
      </Section>
    </Drawer>
  );
}

export function QEvaluatorDrawer({ data, slice, name, onClose }) {
  const t = data.targets;
  const rows = useMemo(() => slice.evals.filter((e) => e.ev === name), [slice, name]);
  const all = useMemo(() => data.evals.filter((e) => e.ev === name), [data, name]);
  const k = useMemo(() => statsOf(rows, t), [rows, t]);
  const months = useMemo(() => monthlySeries(all, t), [all, t]);
  const hist = useMemo(() => histogram(rows, t), [rows, t]);
  const lobs = useMemo(() => groupBy(rows, "lob", t), [rows, t]);
  const params = useMemo(() => paramStats(rows, data.params).filter((p) => p.pass != null).sort((a, b) => a.pass - b.pass).slice(0, 4), [rows, data.params]);
  if (!rows.length) return null;

  return (
    <Drawer testid="q-evaluator-drawer" title={name} sub={`Evaluator${rows[0].evid ? ` #${rows[0].evid}` : ""} · ${lobs.length} LOBs in range`} onClose={onClose}>
      <Chips items={[
        { label: "Evaluations", v: fmtInt(k.evals), color: C.blue }, { label: "Agents covered", v: fmtInt(k.agents), color: C.purple },
        { label: "Avg given", v: fmtN(k.avg), color: C.amber }, { label: "Pass given", v: fmtPct(k.pass), color: C.teal },
      ]} />
      <div className="grid gap-6 lg:grid-cols-2">
        <Section title="Average score given by month">
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={months} margin={{ left: -20, right: 10 }}>
              <XAxis dataKey="m" tick={tickStyle} /><YAxis domain={[0, 100]} tick={tickStyle} />
              <Tooltip contentStyle={tooltipStyle} formatter={(v) => fmtN(v)} />
              <Line type="monotone" dataKey="avg" stroke={C.amber} strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </Section>
        <Section title="Score distribution given">
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={hist} margin={{ left: -20, right: 10 }}>
              <XAxis dataKey="label" tick={tickStyle} /><YAxis tick={tickStyle} />
              <Tooltip contentStyle={tooltipStyle} />
              <Bar dataKey="n" radius={[4, 4, 0, 0]}>{hist.map((h) => <Cell key={h.lo} fill={BAND_COLOR[h.band]} />)}</Bar>
            </BarChart>
          </ResponsiveContainer>
        </Section>
      </div>
      <div className="grid gap-6 lg:grid-cols-2">
        <Section title="LOB mix">
          <div className="space-y-2">{lobs.map((g) => (
            <div key={g.key} className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)_auto] items-center gap-3">
              <p className="truncate text-xs font-semibold text-thstrong">{g.key}</p>
              <div className="h-2 overflow-hidden rounded-full bg-thhover/8"><div className="h-full rounded-full bg-thblue" style={{ width: `${(100 * g.evals) / rows.length}%` }} /></div>
              <span className="mk-sub">{g.evals} · avg {fmtN(g.avg)}</span>
            </div>))}</div>
        </Section>
        <Section title="Lowest-scoring parameters (pass % given)">
          <div className="space-y-2">{params.map((p) => (
            <div key={p.n} className="flex items-center justify-between gap-3 text-xs"><span className="truncate text-thstrong"><span className="font-mono-hud text-thdim">{p.n}.</span> {p.label}</span><span className="font-mono-hud font-semibold" style={{ color: p.pass < 70 ? C.coral : C.amber }}>{fmtPct(p.pass, 0)}</span></div>))}</div>
        </Section>
      </div>
    </Drawer>
  );
}
